import { Flex, Icon, IconButton, Input, Stack, Text } from '@chakra-ui/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

export interface ProductHighlightInputRef {
  resetEditingState: () => void;
}

type HighlightMutationResult = UseMutationResult<
  void,
  AxiosError<{ message: string; statusCode: number }>,
  number,
  unknown
>;

interface ProductHighlightInputProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  name: Path<T>;
  updateMutation: HighlightMutationResult;
  onSaveHighlight: (newValue: number) => void;
  isUpdateDisabled?: boolean;
}

interface DisplayModeProps {
  value: number;
  onEdit: () => void;
  isUpdateDisabled?: boolean;
}

const DisplayMode = ({ value, onEdit, isUpdateDisabled }: DisplayModeProps) => (
  <Flex
    gap={2}
    alignItems="center"
    justifyContent="center"
    w="full"
    opacity={isUpdateDisabled ? 0.5 : 1}
  >
    <Text>{typeof value === 'number' ? value : 0}</Text>
    <IconButton
      aria-label="Edit quantity"
      icon={<Icon as={EditIcon} boxSize={4} />}
      size="sm"
      variant="ghost"
      onClick={onEdit}
      isDisabled={isUpdateDisabled}
    />
  </Flex>
);

interface EditModeProps {
  initialValue: number;
  onSave: (value: number) => void;
  onCancel: () => void;
  updateMutation: HighlightMutationResult;
  name: string;
}

const EditMode = ({
  initialValue,
  onSave,
  onCancel,
  updateMutation,
  name,
}: EditModeProps) => {
  const [tempValue, setTempValue] = useState(initialValue);
  const { isPending, isError } = updateMutation;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.max(0, Number(e.target.value));
    setTempValue(newValue);
  };

  const handleTriggerSave = () => {
    if (isPending || tempValue === initialValue) return;
    onSave(tempValue);
  };

  const handleCancelClick = () => {
    if (isPending) return;
    onCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTriggerSave();
    } else if (e.key === 'Escape') {
      handleCancelClick();
    }
  };

  return (
    <Flex direction="column" alignItems="center" justifyContent="center">
      <Flex gap={2} alignItems="center" justifyContent="center">
        <IconButton
          aria-label="Cancel edit"
          icon={<Icon as={CloseIcon} boxSize={4} color="red.500" />}
          size="sm"
          variant="ghost"
          onClick={handleCancelClick}
          isDisabled={isPending}
          _hover={{
            bg: 'red.50',
          }}
        />

        <Input
          type="number"
          textAlign="center"
          value={tempValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          min={0}
          w="40px"
          h="32px"
          p={1}
          border="1px solid"
          borderColor={isError ? 'red.500' : 'gray.200'}
          isInvalid={isError}
          isDisabled={isPending}
          aria-describedby={isError ? `${name}-error-message` : undefined}
          autoFocus
        />

        <IconButton
          aria-label="Save changes"
          icon={<Icon as={CheckIcon} boxSize={4} color="#38A169" />}
          size="sm"
          variant="ghost"
          onClick={handleTriggerSave}
          isDisabled={isPending || tempValue === initialValue}
          isLoading={isPending}
          _hover={{
            bg: '#F0FFF4',
          }}
        />
      </Flex>

      {isError && updateMutation.error?.response?.data?.message && (
        <Text fontSize="xs" color="red.500" id={`${name}-error-message`}>
          {updateMutation.error.response.data.statusCode === 422
            ? 'Melebihi Kuantitas'
            : updateMutation.error.response.data.message}
        </Text>
      )}
      {isError && !updateMutation.error?.response?.data?.message && (
        <Text fontSize="xs" color="red.500" id={`${name}-error-message`}>
          Gagal update
        </Text>
      )}
    </Flex>
  );
};

export const ProductHighlightInput = forwardRef(function ProductHighlightInput<
  T extends FieldValues = FieldValues
>(
  {
    control,
    name,
    updateMutation,
    onSaveHighlight,
    isUpdateDisabled,
  }: ProductHighlightInputProps<T>,
  ref: React.Ref<ProductHighlightInputRef>
) {
  const [isEditing, setIsEditing] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      resetEditingState: () => {
        setIsEditing(false);
      },
    }),
    [setIsEditing]
  );

  const handleEnterEditMode = () => setIsEditing(true);
  const handleExitEditMode = () => setIsEditing(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        min: 0,
      }}
      render={({ field: { value } }) => (
        <Stack w="full">
          {isEditing ? (
            <EditMode
              initialValue={value}
              onSave={onSaveHighlight}
              onCancel={handleExitEditMode}
              updateMutation={updateMutation}
              name={name}
            />
          ) : (
            <DisplayMode
              value={value}
              onEdit={handleEnterEditMode}
              isUpdateDisabled={isUpdateDisabled}
            />
          )}
        </Stack>
      )}
    />
  );
});

ProductHighlightInput.displayName = 'ProductHighlightInput';
